/**
 * Bandeja de autorizaciones (spec 01, enmienda 2026-10-09): las solicitudes a distancia que este usuario puede
 * aprobar o negar, y el historial de todo lo autorizado (con clave o a distancia) y por quién.
 */
import type { AutorizacionDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { FiltroFechas } from '@/componentes/FiltroFechas.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

interface Pagina { datos: AutorizacionDTO[]; total: number }
const ESTADO: Record<AutorizacionDTO['estado'], { texto: string; v: 'ok' | 'pendiente' | 'critico' | 'neutro' | 'info' }> = {
  pendiente: { texto: 'Pendiente', v: 'pendiente' },
  aprobada: { texto: 'Aprobada', v: 'ok' },
  usada: { texto: 'Usada', v: 'info' },
  negada: { texto: 'Negada', v: 'critico' },
  vencida: { texto: 'Vencida', v: 'neutro' },
};

export function detalleTexto(detalle: AutorizacionDTO['detalle']): string {
  if (!detalle) return '';
  return Object.entries(detalle).filter(([, v]) => v !== null && v !== '').map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`).join(' · ');
}

export function Autorizaciones() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('');
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [negar, setNegar] = useState<AutorizacionDTO | null>(null);
  const [comentario, setComentario] = useState('');
  const pendientes = useQuery({ queryKey: ['autorizaciones', 'pendientes'], queryFn: () => get<Pagina>('/autorizaciones', { estado: 'pendiente', limite: 50 }), refetchInterval: 10_000 });
  const historial = useQuery({ queryKey: ['autorizaciones', 'historial', estado, rango], queryFn: () => get<Pagina>('/autorizaciones', { estado: estado || undefined, desde: rango.desde || undefined, hasta: rango.hasta || undefined, limite: 100 }), placeholderData: (p) => p });
  const resolver = useMutation({
    mutationFn: ({ id, decision, comentario: c }: { id: string; decision: 'aprobada' | 'negada'; comentario?: string }) => post<AutorizacionDTO>(`/autorizaciones/${id}/resolver`, { decision, ...(c ? { comentario: c } : {}) }),
    onSuccess: async (a) => {
      toast.success(a.estado === 'aprobada' ? `Autorizado a ${a.solicitante_nombre}` : `Negado a ${a.solicitante_nombre}`);
      setNegar(null);
      setComentario('');
      await qc.invalidateQueries({ queryKey: ['autorizaciones'] });
      await qc.invalidateQueries({ queryKey: ['notificaciones'] });
    },
    onError: (e) => toast.error(mensajeDeError(e)),
  });

  return (
    <>
      <TituloPagina titulo="Autorizaciones" subtitulo="Solicitudes que puedes aprobar o negar, y todo lo que se ha autorizado. Las autorizaciones dadas con clave en la caja también quedan aquí." />
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-mg-verde" /> Pendientes {pendientes.data?.total ? <Badge variant="pendiente">{pendientes.data.total}</Badge> : null}</CardTitle>
          <CardDescription>Se actualiza sola cada 10 segundos. Una solicitud sin respuesta vence y debe pedirse de nuevo.</CardDescription>
        </CardHeader>
        <CardContent>
          <EstadoQuery cargando={pendientes.isLoading} error={pendientes.error} datos={pendientes.data} reintentar={() => void pendientes.refetch()}>
            {(p) =>
              p.datos.length === 0 ? (
                <Vacio titulo="Sin solicitudes pendientes" descripcion="Cuando un cajero pida una autorización a distancia aparecerá aquí." className="py-6" />
              ) : (
                <ul className="divide-y" data-testid="autorizaciones-pendientes">
                  {p.datos.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`autorizacion-${a.tipo}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{a.tipo_etiqueta} <span className="font-normal text-muted-foreground">· {a.solicitante_nombre} · {a.sucursal_nombre}</span></p>
                        <p className="text-sm">{a.motivo}{a.monto_usd ? ` · $ ${a.monto_usd}` : ''}</p>
                        {a.detalle ? <p className="text-xs text-muted-foreground">{detalleTexto(a.detalle)}</p> : null}
                        <p className="text-[11px] text-muted-foreground">Pedida {formatearFechaHora(a.creado_en)} · vence {formatearFechaHora(a.caduca_en)}</p>
                      </div>
                      {a.puedo_resolver ? (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => resolver.mutate({ id: a.id, decision: 'aprobada' })} disabled={resolver.isPending} data-testid="autorizacion-aprobar"><Check /> Aprobar</Button>
                          <Button size="sm" variant="outline" onClick={() => setNegar(a)} disabled={resolver.isPending} data-testid="autorizacion-negar"><X /> Negar</Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )
            }
          </EstadoQuery>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row flex-wrap items-end justify-between gap-2">
          <div>
            <CardTitle>Historial</CardTitle>
            <CardDescription>Quién pidió, quién autorizó, por qué vía y en qué operación se usó.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-40" aria-label="Estado">
              <option value="">Todos los estados</option>
              {Object.entries(ESTADO).map(([k, v]) => <option key={k} value={k}>{v.texto}</option>)}
            </Select>
            <FiltroFechas desde={rango.desde} hasta={rango.hasta} onChange={(desde, hasta) => setRango({ desde, hasta })} testid="autorizaciones" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <EstadoQuery cargando={historial.isLoading} error={historial.error} datos={historial.data} reintentar={() => void historial.refetch()}>
            {(h) =>
              h.datos.length === 0 ? (
                <Vacio titulo="Sin autorizaciones en ese rango" className="m-4 py-6" />
              ) : (
                <Table data-testid="autorizaciones-historial">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Qué</TableHead>
                      <TableHead>Pidió</TableHead>
                      <TableHead>Autorizó</TableHead>
                      <TableHead>Vía</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {h.datos.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="whitespace-nowrap">{formatearFechaHora(a.creado_en)}</TableCell>
                        <TableCell>
                          <p className="font-medium">{a.tipo_etiqueta}</p>
                          <p className="text-xs text-muted-foreground">{a.motivo}{a.detalle ? ` · ${detalleTexto(a.detalle)}` : ''}{a.comentario ? ` · “${a.comentario}”` : ''}</p>
                        </TableCell>
                        <TableCell>{a.solicitante_nombre}<span className="block text-[11px] text-muted-foreground">{a.sucursal_nombre}</span></TableCell>
                        <TableCell>{a.resuelta_por_nombre ?? '—'}</TableCell>
                        <TableCell>{a.via === 'clave' ? 'Clave' : 'A distancia'}</TableCell>
                        <TableCell><Badge variant={ESTADO[a.estado].v}>{ESTADO[a.estado].texto}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            }
          </EstadoQuery>
        </CardContent>
      </Card>

      <Dialog open={!!negar} onOpenChange={(o) => !o && setNegar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Negar autorización</DialogTitle>
            <DialogDescription>{negar?.tipo_etiqueta} · {negar?.solicitante_nombre}. El motivo le llega al solicitante.</DialogDescription>
          </DialogHeader>
          <Campo etiqueta="Por qué se niega" htmlFor="neg-comentario">
            <Input id="neg-comentario" autoFocus value={comentario} onChange={(e) => setComentario(e.target.value)} data-testid="autorizacion-comentario" />
          </Campo>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNegar(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={comentario.trim().length < 3} cargando={resolver.isPending} onClick={() => negar && resolver.mutate({ id: negar.id, decision: 'negada', comentario: comentario.trim() })} data-testid="autorizacion-negar-confirmar">Negar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
